import { API_BASE, authHeaders } from './apiAuth'
import type { ImageRef } from './imageRefs'

/**
 * Video Generation, as the page, the board's card and the Miro panel see it
 * (workers/api/src/routes/video.ts). A run is a skill run like a picture's:
 * started with POST /api/skills/run, followed with GET /api/skills/run?id=,
 * listed with GET /api/skills/runs.
 */

export interface VideoModel {
  key: string
  label: string
  provider: 'kling' | 'fal'
  resolution: string
  minSeconds: number
  maxSeconds: number
  defaultSeconds: number
  blurb: string
  /** Its key is on the server. */
  ready: boolean
  /** Dollars for one second, silent and with sound. */
  perSecond: { silent: number; audio: number }
  /** How many frames it takes between the first and the last (Kling v4: 8). */
  maxMiddle?: number
}

/** One part of a video told part by part (Kling v4): what happens, and for how long. */
export interface VideoPart {
  prompt: string
  seconds: number
}

export interface VideoAsk {
  projectId: string
  firstFrame: ImageRef
  lastFrame?: ImageRef
  /** Frames between the first and the last, in order (Kling v4, the board's card). */
  middleFrames?: ImageRef[]
  /** What happens part by part, one part more than the frames between; `seconds` is then their sum. */
  parts?: VideoPart[]
  model: string
  seconds: number
  audio: boolean
  prompt: string
}

export interface VideoStarted {
  runId: string
  /** Runs going for this person now, this one included, and the most there may be. */
  live: number
  limit: number
}

export interface VideoClip {
  id: string
  /** Signed for two hours. */
  url: string
  title: string
}

export interface VideoRun {
  id: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  error: string
  credits: number
  model: string
  seconds: number
  audio: boolean
  prompt: string
  estimateUsd: number
  createdAt: string
  video?: VideoClip
  /** The frames that went in, signed. */
  frames: { first?: string; last?: string }
}

async function reason(response: Response): Promise<string> {
  const body = (await response.json().catch(() => null)) as { error?: string } | null
  return body?.error ?? `HTTP ${response.status}`
}

export async function videoOptions(): Promise<VideoModel[]> {
  const response = await fetch(`${API_BASE}/api/video/options`, { headers: await authHeaders() })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as { models?: VideoModel[] }
  return body.models ?? []
}

/** Dollars a video will cost, by the second (fal's 16:9 figure for Seedance: a squarer frame costs a little less). */
export function estimateVideo(model: VideoModel, seconds: number, audio: boolean): number {
  return seconds * (audio ? model.perSecond.audio : model.perSecond.silent)
}

export async function startVideo(ask: VideoAsk): Promise<VideoStarted> {
  const response = await fetch(`${API_BASE}/api/skills/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({
      skill: 'skill-video-generation',
      projectId: ask.projectId,
      inputs: [ask.firstFrame, ...(ask.middleFrames ?? []), ...(ask.lastFrame ? [ask.lastFrame] : [])],
      params: {
        model: ask.model,
        seconds: ask.seconds,
        audio: ask.audio,
        prompt: ask.prompt,
        // Said only when there are frames between, so an older server reads the rest as before.
        ...(ask.middleFrames && ask.middleFrames.length > 0
          ? { middles: ask.middleFrames.length, hasLast: Boolean(ask.lastFrame) }
          : {}),
        ...(ask.parts && ask.parts.length > 0 ? { parts: ask.parts } : {}),
      },
    }),
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as { runId?: string; live?: number; limit?: number }
  if (!body.runId) throw new Error('The video did not start')
  return { runId: body.runId, live: Number(body.live) || 0, limit: Number(body.limit) || 0 }
}

/**
 * A signed link that saves the file rather than playing it: Supabase's own
 * `download` parameter, which makes the store say "attachment". The `download`
 * attribute on a link is ignored for another site's file, so this is what makes
 * Download download -- on the page and inside the Miro panel alike.
 */
export function downloadLink(url: string, name: string): string {
  if (!url) return ''
  return `${url}${url.includes('?') ? '&' : '?'}download=${encodeURIComponent(name)}`
}

/** A file name for a video: what it is, and when. */
export function videoFileName(run: { model: string; seconds: number; createdAt: string }): string {
  const day = (run.createdAt || new Date().toISOString()).slice(0, 16).replace(/[-:T]/g, '')
  return `wonderworks-${run.model || 'video'}-${run.seconds || ''}s-${day}.mp4`
}

interface RawPicture {
  id?: string
  store?: string
  originalPath?: string
  thumbnailPath?: string
  title?: string
}

function shape(row: Record<string, unknown>): VideoRun {
  const params = (row.params ?? {}) as Record<string, unknown>
  const made = (Array.isArray(row.pictures) ? row.pictures : []) as RawPicture[]
  const references = (Array.isArray(row.references) ? row.references : []) as RawPicture[]
  const inputs = (Array.isArray(row.inputs) ? row.inputs : []).map(String)
  const linkOf = (name: string | undefined) => {
    if (!name) return undefined
    const id = name.slice(name.indexOf(':') + 1)
    const found = references.find((one) => one.id === id)
    // The smaller copy where there is one: these are drawn small.
    return found?.thumbnailPath || found?.originalPath
  }
  const clip = made[0]
  return {
    id: String(row.id ?? ''),
    status: (['queued', 'running', 'succeeded', 'failed'].includes(String(row.status)) ? row.status : 'queued') as VideoRun['status'],
    error: String(row.error ?? ''),
    credits: Number(row.credits) || 0,
    model: String(params.model ?? ''),
    seconds: Number(params.seconds) || 0,
    audio: params.audio === true,
    prompt: String(params.prompt ?? ''),
    estimateUsd: Number(params.estimateUsd) || 0,
    createdAt: String(row.createdAt ?? ''),
    video: clip?.originalPath ? { id: String(clip.id ?? ''), url: clip.originalPath, title: String(clip.title ?? '') } : undefined,
    // The last is after any frames between (Kling v4).
    frames: {
      first: linkOf(inputs[0]),
      last:
        params.middles === undefined
          ? linkOf(inputs[1])
          : params.hasLast === true
            ? linkOf(inputs[1 + (Number(params.middles) || 0)])
            : undefined,
    },
  }
}

/** The person's videos in one project, newest first. */
export async function listVideos(projectId: string): Promise<VideoRun[]> {
  const response = await fetch(
    `${API_BASE}/api/skills/runs?project=${encodeURIComponent(projectId)}&skill=video-generation`,
    { headers: { Accept: 'application/json', ...(await authHeaders()) } },
  )
  if (!response.ok) throw new Error(await reason(response))
  const rows = (await response.json()) as Record<string, unknown>[]
  return rows.map(shape)
}

/** How a video run ended, for the board's card. */
export interface VideoDone {
  status: 'succeeded' | 'failed'
  /** The stored video, `project:<id>`, when there is one. */
  output?: ImageRef
  /** Its signed link. */
  url: string
  error: string
  credits: number
  live: number
  limit: number
}

/** Seconds between asks. A video takes minutes; asking every few seconds buys nothing. */
const ASK_EVERY = 6000
/** The server stops waiting at ninety minutes (routes/video.ts); this a little after. */
const GIVE_UP_AFTER = 95 * 60 * 1000
const MAX_MISSES = 10

/**
 * Wait for a video run to finish. Not the pictures' awaitRun: that gives up
 * at twelve minutes, and a 15-second 4K video can take longer than that.
 */
export async function awaitVideo(runId: string): Promise<VideoDone> {
  const startedAt = Date.now()
  let misses = 0
  const failed = (error: string): VideoDone => ({ status: 'failed', url: '', error, credits: 0, live: 0, limit: 0 })
  for (;;) {
    await new Promise((wake) => setTimeout(wake, ASK_EVERY))
    try {
      const response = await fetch(`${API_BASE}/api/skills/run?id=${encodeURIComponent(runId)}`, {
        headers: await authHeaders(),
      })
      if (response.status === 404) return failed('This video run is gone')
      if (!response.ok) throw new Error(await reason(response))
      const body = (await response.json()) as Record<string, unknown>
      misses = 0
      const status = String(body.status ?? '')
      if (status === 'succeeded' || status === 'failed') {
        const outputs = (Array.isArray(body.outputs) ? body.outputs : []).map(String)
        const first = outputs[0] ?? ''
        const colon = first.indexOf(':')
        const pictures = (Array.isArray(body.pictures) ? body.pictures : []) as RawPicture[]
        return {
          status,
          output: colon > 0 && first.slice(0, colon) === 'project' ? { store: 'project', id: first.slice(colon + 1) } : undefined,
          url: String(pictures[0]?.originalPath ?? ''),
          error: String(body.error ?? ''),
          credits: Number(body.credits) || 0,
          live: Number(body.live) || 0,
          limit: Number(body.limit) || 0,
        }
      }
    } catch (error) {
      misses += 1
      console.warn('could not read the video run', error)
      if (misses >= MAX_MISSES) return failed('Lost track of this video — it may still finish; see the Video page')
    }
    if (Date.now() - startedAt > GIVE_UP_AFTER) return failed('This took longer than it should have — see the Video page')
  }
}
